import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import PdfLayout from "../pdf/PdfLayout";
import MarkdownRenderer from "../custom-markdown";
import { AiSupportedTasksResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  // Base section styles
  section: {
    marginBottom: 20,
    padding: 16,
    borderRadius: 8,
  },
  // Description section (green theme)
  descriptionSection: {
    backgroundColor: "#F0FDF4",
    borderColor: "#BBF7D0",
    borderWidth: 1,
  },
  descriptionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#16A34A",
    paddingBottom: 5,
    borderBottom: "1 solid #BBF7D0",
  },
  // Learning objectives section (blue theme)
  learningObjectivesSection: {
    backgroundColor: "#EFF6FF",
    borderColor: "#BFDBFE",
    borderWidth: 1,
  },
  learningObjectivesTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#2563EB",
    paddingBottom: 5,
    borderBottom: "1 solid #BFDBFE",
  },
  // Steps section (green theme)
  stepsSection: {
    backgroundColor: "#F0FDF4",
    borderColor: "#BBF7D0",
    borderWidth: 1,
  },
  stepsTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#16A34A",
    paddingBottom: 5,
    borderBottom: "1 solid #BBF7D0",
  },
  // Rubric section (amber theme)
  rubricSection: {
    backgroundColor: "#FFFBEB",
    borderColor: "#FDE68A",
    borderWidth: 1,
  },
  rubricTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#D97706",
    paddingBottom: 5,
    borderBottom: "1 solid #FDE68A",
  },
  // Notes section (purple theme)
  notesSection: {
    backgroundColor: "#F5F3FF",
    borderColor: "#DDD6FE",
    borderWidth: 1,
  },
  notesTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#7C3AED",
    paddingBottom: 5,
    borderBottom: "1 solid #DDD6FE",
  },
  // Common styles
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
    paddingLeft: 10,
  },
  bullet: {
    marginRight: 8,
    color: "#16A34A",
  },
  stepContainer: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  stepHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  stepNumber: {
    width: 24,
    height: 24,
    backgroundColor: "#16A34A",
    color: "white",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 12,
    padding: 4,
    marginRight: 8,
  },
  stepTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#16A34A",
  },
  promptsContainer: {
    marginTop: 8,
    paddingLeft: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 4,
    padding: 8,
  },
  promptsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#047857",
    marginBottom: 4,
  },
  promptItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
    backgroundColor: "#FFFFFF",
    padding: 6,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  promptText: {
    fontSize: 10,
    color: "#374151",
  },
  rubricContainer: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#FDE68A",
  },
  rubricDescription: {
    fontSize: 12,
    color: "#374151",
    marginBottom: 8,
  },
  levelContainer: {
    marginBottom: 8,
    padding: 8,
    backgroundColor: "#FFFBEB",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#FDE68A",
  },
  levelHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  levelScore: {
    width: 24,
    height: 24,
    backgroundColor: "#D97706",
    color: "white",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 12,
    padding: 4,
    marginRight: 8,
  },
  levelScoreText: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#D97706",
  },
  levelDescription: {
    marginTop: 4,
    paddingLeft: 4,
  },
  notesSectionContainer: {
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDD6FE",
    padding: 12,
  },
  sectionContent: {
    fontSize: 11,
    lineHeight: 1.4,
    color: "#374151",
  },
  // Updated and new rubric styles
  rubricLevelsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 8,
  },
  // Level 3 - Excellent (Green theme)
  excellentLevelContainer: {
    flex: 1,
    minWidth: "30%",
    backgroundColor: "#F0FDF4",
    borderColor: "#BBF7D0",
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  excellentHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#BBF7D0",
    paddingBottom: 6,
    marginBottom: 6,
  },
  excellentHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 8,
  },
  excellentIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#22C55E",
    marginRight: 6,
  },
  excellentText: {
    color: "#16A34A",
    fontSize: 12,
    fontWeight: "bold",
    flex: 1,
  },
  // Level 2 - Satisfactory (Amber theme)
  satisfactoryLevelContainer: {
    flex: 1,
    minWidth: "30%",
    backgroundColor: "#FFFBEB",
    borderColor: "#FDE68A",
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  satisfactoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#FDE68A",
    paddingBottom: 6,
    marginBottom: 6,
  },
  satisfactoryHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 8,
  },
  satisfactoryIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#F59E0B",
    marginRight: 6,
  },
  satisfactoryText: {
    color: "#D97706",
    fontSize: 12,
    fontWeight: "bold",
    flex: 1,
  },
  // Level 1 - Needs Improvement (Red theme)
  needsImprovementLevelContainer: {
    flex: 1,
    minWidth: "30%",
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  needsImprovementHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#FECACA",
    paddingBottom: 6,
    marginBottom: 6,
  },
  needsImprovementHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 8,
  },
  needsImprovementIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#EF4444",
    marginRight: 6,
  },
  needsImprovementText: {
    color: "#DC2626",
    fontSize: 12,
    fontWeight: "bold",
    flex: 1,
  },
  scoreText: {
    fontSize: 14,
    fontWeight: "bold",
    flexShrink: 0,
    width: 20,
    textAlign: "right",
  },
  levelDetailText: {
    fontSize: 11,
    color: "#374151",
    marginTop: 4,
  },
});

interface PDFProps {
  tool: AiSupportedTasksResponseDto & {
    imageUrl?: string;
  };
  customizationSettings: PdfCustomizationSettings;
}

const AiSupportedTasksCardPDF = ({ tool, customizationSettings }: PDFProps) => {
  const { t } = useTranslation();

  const markdownSectionContentStyle = {
    text: {
      text: {
        ...styles.sectionContent,
        fontSize: scaleFontSize(
          styles.sectionContent.fontSize,
          customizationSettings.fontSize,
        ),
      },
    },
  };

  return (
    <PdfLayout
      courseName={tool.courseName}
      gradeLevel={Number(tool.gradeLevel) || 0}
      topic={tool.title || tool.prompt || ""}
      imageUrl={tool.imageUrl}
      headerTextColor="#047857"
      title={tool.title}
      author={tool.authorName}
      subject={tool.courseName}
      createdDate={tool.createdAt}
      customizationSettings={customizationSettings}
    >
      {/* Description Section */}
      {tool.description && (
        <View style={[styles.section, styles.descriptionSection]}>
          <Text
            style={[
              styles.descriptionTitle,
              {
                fontSize: scaleFontSize(
                  styles.descriptionTitle.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("common.description")}
          </Text>
          <MarkdownRenderer
            content={tool.description}
            customStyle={markdownSectionContentStyle}
          />
        </View>
      )}

      {/* Learning Objectives Section */}
      {tool.learningObjectives && tool.learningObjectives.length > 0 && (
        <View
          style={[styles.section, styles.learningObjectivesSection]}
          wrap={false}
        >
          <Text
            style={[
              styles.learningObjectivesTitle,
              {
                fontSize: scaleFontSize(
                  styles.learningObjectivesTitle.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("common.learningObjectives")}
          </Text>
          {tool.learningObjectives.map((objective: string, index: number) => (
            <View key={index} style={styles.listItem}>
              <Text style={[styles.bullet, { color: "#2563EB" }]}>•</Text>
              <MarkdownRenderer
                content={objective}
                customStyle={markdownSectionContentStyle}
              />
            </View>
          ))}
        </View>
      )}

      {/* Steps Section */}
      {tool.steps && tool.steps.length > 0 && (
        <View style={[styles.section, styles.stepsSection]}>
          <Text
            style={[
              styles.stepsTitle,
              {
                fontSize: scaleFontSize(
                  styles.stepsTitle.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("common.steps")}
          </Text>
          {tool.steps.map((step: any, index: number) => (
            <View key={index} style={styles.stepContainer} wrap={false}>
              <View style={styles.stepHeader}>
                <Text style={styles.stepNumber}>{step.id}</Text>
                <Text
                  style={[
                    styles.stepTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.stepTitle.fontSize,
                        customizationSettings.fontSize,
                      ),
                    },
                  ]}
                >
                  {step.title}
                </Text>
              </View>
              <MarkdownRenderer
                content={step.description}
                customStyle={markdownSectionContentStyle}
              />
              {step.examplePrompts && step.examplePrompts.length > 0 && (
                <View style={styles.promptsContainer} wrap={false}>
                  <Text
                    style={[
                      styles.promptsTitle,
                      {
                        fontSize: scaleFontSize(
                          styles.promptsTitle.fontSize,
                          customizationSettings.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("common.examplePrompts")}:
                  </Text>
                  {step.examplePrompts.map(
                    (prompt: string, promptIndex: number) => (
                      <View key={promptIndex} style={styles.promptItem}>
                        <Text style={[styles.bullet, { color: "#047857" }]}>
                          •
                        </Text>
                        <Text
                          style={[
                            styles.promptText,
                            {
                              fontSize: scaleFontSize(
                                styles.promptText.fontSize,
                                customizationSettings.fontSize,
                              ),
                            },
                          ]}
                        >
                          {prompt}
                        </Text>
                      </View>
                    ),
                  )}
                </View>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Assessment Rubric Section */}
      {tool.assessmentRubric && tool.assessmentRubric.length > 0 && (
        <View style={[styles.section, styles.rubricSection]}>
          <Text
            style={[
              styles.rubricTitle,
              {
                fontSize: scaleFontSize(
                  styles.rubricTitle.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("common.assessmentRubric")}
          </Text>
          {tool.assessmentRubric.map((rubric: any, index: number) => (
            <View key={index} style={styles.rubricContainer} wrap={false}>
              <Text
                style={[
                  styles.rubricTitle,
                  {
                    fontSize: scaleFontSize(14, customizationSettings.fontSize),
                  },
                ]}
              >
                {rubric.name}
              </Text>
              <Text
                style={[
                  styles.rubricDescription,
                  {
                    fontSize: scaleFontSize(
                      styles.rubricDescription.fontSize,
                      customizationSettings.fontSize,
                    ),
                  },
                ]}
              >
                {rubric.description}
              </Text>
              <View style={styles.rubricLevelsContainer}>
                {rubric.levels
                  ?.sort(
                    (a: { score?: number }, b: { score?: number }) =>
                      (b.score || 0) - (a.score || 0),
                  )
                  .map((level: any, levelIndex: number) => {
                    // Extract level name from description if it follows the pattern "LevelName - Description"
                    let levelDetail = level.description || "";
                    const dashIndex = levelDetail.indexOf(" - ");
                    if (dashIndex > 0) {
                      levelDetail = levelDetail.substring(dashIndex + 3);
                    }

                    const score = level.score || 0;
                    let containerStyle,
                      headerStyle,
                      indicatorStyle,
                      textStyle,
                      labelText;

                    if (score === 3) {
                      containerStyle = styles.excellentLevelContainer;
                      headerStyle = styles.excellentHeader;
                      indicatorStyle = styles.excellentIndicator;
                      textStyle = styles.excellentText;
                      labelText = t("aiSupportedTasks.excellent");
                    } else if (score === 2) {
                      containerStyle = styles.satisfactoryLevelContainer;
                      headerStyle = styles.satisfactoryHeader;
                      indicatorStyle = styles.satisfactoryIndicator;
                      textStyle = styles.satisfactoryText;
                      labelText = t("aiSupportedTasks.satisfactory");
                    } else {
                      containerStyle = styles.needsImprovementLevelContainer;
                      headerStyle = styles.needsImprovementHeader;
                      indicatorStyle = styles.needsImprovementIndicator;
                      textStyle = styles.needsImprovementText;
                      labelText = t("aiSupportedTasks.needsImprovements");
                    }

                    return (
                      <View key={levelIndex} style={containerStyle}>
                        <View style={headerStyle}>
                          <View
                            style={
                              score === 3
                                ? styles.excellentHeaderLeft
                                : score === 2
                                  ? styles.satisfactoryHeaderLeft
                                  : styles.needsImprovementHeaderLeft
                            }
                          >
                            <View style={indicatorStyle} />
                            <Text
                              style={[
                                textStyle,
                                {
                                  fontSize: scaleFontSize(
                                    textStyle.fontSize,
                                    customizationSettings.fontSize,
                                  ),
                                },
                              ]}
                            >
                              {labelText}
                            </Text>
                          </View>
                        </View>
                        <Text
                          style={[
                            styles.levelDetailText,
                            {
                              fontSize: scaleFontSize(
                                styles.levelDetailText.fontSize,
                                customizationSettings.fontSize,
                              ),
                            },
                          ]}
                        >
                          {levelDetail}
                        </Text>
                      </View>
                    );
                  })}
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Notes Section */}
      {(tool.notes?.forTeachers?.length || tool.notes?.forStudents?.length) && (
        <View style={[styles.section, styles.notesSection]} break>
          <Text
            style={[
              styles.notesTitle,
              {
                fontSize: scaleFontSize(
                  styles.notesTitle.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("common.notes")}
          </Text>
          {tool.notes?.forTeachers && tool.notes.forTeachers.length > 0 && (
            <View style={styles.notesSectionContainer}>
              <Text
                style={[
                  styles.notesTitle,
                  {
                    fontSize: scaleFontSize(14, customizationSettings.fontSize),
                  },
                ]}
              >
                {t("common.forTeachers")}
              </Text>
              {tool.notes.forTeachers.map((note: string, index: number) => (
                <View key={index} style={styles.listItem}>
                  <Text style={[styles.bullet, { color: "#7C3AED" }]}>•</Text>
                  <MarkdownRenderer
                    content={note}
                    customStyle={markdownSectionContentStyle}
                  />
                </View>
              ))}
            </View>
          )}
          {tool.notes?.forStudents && tool.notes.forStudents.length > 0 && (
            <View style={styles.notesSectionContainer}>
              <Text
                style={[
                  styles.notesTitle,
                  {
                    fontSize: scaleFontSize(14, customizationSettings.fontSize),
                  },
                ]}
              >
                {t("common.forStudents")}
              </Text>
              {tool.notes.forStudents.map((note: string, index: number) => (
                <View key={index} style={styles.listItem}>
                  <Text style={[styles.bullet, { color: "#7C3AED" }]}>•</Text>
                  <MarkdownRenderer
                    content={note}
                    customStyle={markdownSectionContentStyle}
                  />
                </View>
              ))}
            </View>
          )}
        </View>
      )}
    </PdfLayout>
  );
};

export default AiSupportedTasksCardPDF;
